import { Stack } from '@mui/material';
import { type SetStateAction, useCallback, useMemo, useState } from 'react';

import { _getAdminViewsUsersInfinite } from '@/api/admin/infinite-queries';
import { AdminDashboardUserStatus, AdminUsersSortField, AdminViewsUser } from '@/api/admin/types';
import { SortDirection, SortDirectionType } from '@/api/types';
import { getTableMinWidth, Spinner, Table } from '@/design-system';
import useDebounce from '@/global-hooks/use-debounce';
import { useUrlSyncedState } from '@/global-hooks/use-url-synced-state';
import { useInfiniteScroll } from '@/shared/hooks/use-infinite-scroll';
import { Permission, useCan } from '@/shared/modules/permissions';

import { usersTableColumns } from '../data';
import { UserTabFilters, UserTabFiltersValue } from './user-tab-filters';
import { toUserTabTableRow, UserTabTableRow } from './user-tab-table.utils';

type UserTableVisibleColumnKey = keyof AdminViewsUser | 'verifiedAt' | 'actions' | 'status';

const URL_KEYS = [
  'search',
  'sortBy',
  'sortDirection',
  'status',
  'gammaTeam',
  'entityAffiliation',
  'tags',
] as const;

export const UserTabTable = () => {
  const {
    state,
    setMany,
    sortBy: sortByRaw,
    sortDirection: sortDirectionRaw,
  } = useUrlSyncedState(URL_KEYS);

  const sortBy = sortByRaw || null;
  const sortDirection = (sortDirectionRaw as SortDirectionType) || null;

  const [visibleColumns, setVisibleColumns] = useState<UserTableVisibleColumnKey[]>([
    'name',
    'email',
    'status',
    'entityAffiliation',
    'isGammaTeam',
    'currentIntroducer',

    'countryOfResidence',
    'city',
    'phoneNumber',
    'nationalities',
    'isUsPerson',
    'isPoliticallyExposedPerson',
    'businessOrOccupation',
    'netWorth',
    'householdGrossIncome',
    'fundsInvestedCount',
    'totalAmountInvestedUsd',
    'isIntroducer',
    'questionnaireSubmitted',
    'kycStatus',

    'verifiedAt',
    'actions',
  ]);

  const canFilterByTags = useCan(Permission.UserAdminManage);

  const filters: UserTabFiltersValue = useMemo(
    () => ({
      search: state.search[0] ?? '',
      status: state.status as AdminDashboardUserStatus[],
      entityAffiliation: state.entityAffiliation.map((v) => v === 'yes'),
      gammaTeam: state.gammaTeam.map((v) => v === 'yes'),
      tags: canFilterByTags ? (state.tags as string[]) : [],
      columns: visibleColumns,
    }),
    [
      state.search,
      state.status,
      state.entityAffiliation,
      state.gammaTeam,
      state.tags,
      canFilterByTags,
      visibleColumns,
    ],
  );

  const setFilters = useCallback(
    (action: SetStateAction<UserTabFiltersValue>) => {
      const next = typeof action === 'function' ? action(filters) : action;
      setMany({
        search: next.search || undefined,
        status: next.status,
        gammaTeam: next.gammaTeam.map((v) => (v ? 'yes' : 'no')),
        entityAffiliation: next.entityAffiliation.map((v) => (v ? 'yes' : 'no')),
        tags: next.tags,
      });
      setVisibleColumns(next.columns as UserTableVisibleColumnKey[]);
    },
    [filters, setMany],
  );

  const debounceSearch = useDebounce(filters.search, 500);

  const {
    items: users,
    isLoading,
    isFetchingNextPage,
    loadMoreRef,
  } = useInfiniteScroll(
    _getAdminViewsUsersInfinite({
      sortBy: (sortBy as AdminUsersSortField) || undefined,
      sortDirection: sortDirection ? (sortDirection.toLowerCase() as 'asc' | 'desc') : undefined,
      search: debounceSearch?.length > 1 ? debounceSearch : undefined,
      status: filters.status,
      gammaTeam: filters.gammaTeam,
      hasEntityAffiliation: filters.entityAffiliation,
      tags: filters.tags.length > 0 ? filters.tags : undefined,
    }),
    (page) => page.items,
  );

  const filterColumns = useMemo(
    () =>
      usersTableColumns.filter((column) =>
        visibleColumns.includes(column.key as UserTableVisibleColumnKey),
      ),
    [visibleColumns],
  );

  const userRows: UserTabTableRow[] = useMemo(() => users.map(toUserTabTableRow), [users]);

  const handleSort = useCallback(
    (key: string) => {
      if (sortBy !== key) {
        setMany({ sortBy: key, sortDirection: SortDirection.ASC });
      } else if (sortDirection === SortDirection.ASC) {
        setMany({ sortDirection: SortDirection.DESC });
      } else {
        setMany({ sortBy: undefined, sortDirection: undefined });
      }
    },
    [sortBy, sortDirection, setMany],
  );

  return (
    <Stack flexDirection="column" alignItems="center" gap="24px">
      <UserTabFilters value={filters} setValue={setFilters} />

      <Table
        columns={filterColumns}
        rows={userRows}
        size="lg"
        sortKey={sortBy}
        sortDirection={sortDirection}
        onSort={handleSort}
        isLoading={isLoading}
        minTableWidth={getTableMinWidth(filterColumns)}
      />

      <div ref={loadMoreRef} style={{ height: '20px' }} />

      {isFetchingNextPage && (
        <Stack padding="16px" alignItems="center" justifyContent="center">
          <Spinner />
        </Stack>
      )}
    </Stack>
  );
};
